import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Panel } from "@/components/extranet/StatusBadge";
import { PROMO_GROUPS, PROMO_TYPES, PromoTypeRow } from "@/components/extranet/PromoTypes";
import PromotionDialog from "@/components/extranet/PromotionDialog";
import { http, errMsg, fmtDate } from "@/lib/api";
import { useT } from "@/lib/i18n";

const range = (a, b) => (a || b ? `${a ? fmtDate(a, "d MMM yy") : "…"} – ${b ? fmtDate(b, "d MMM yy") : "…"}` : "Kapan saja");
const condition = (p) => ({
  last_minute: `≤ H-${p.last_minute_days}`, early_bird: `≥ H-${p.early_bird_days}`, geo: p.countries?.join(", "),
  promo_code: p.code, mobile: "Pemesan via HP", member: "Member masuk",
}[p.kind] || "-");

export default function Promotions() {
  const { property } = useOutletContext();
  const t = useT();
  const [list, setList] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [plans, setPlans] = useState([]);
  const [dlg, setDlg] = useState({ open: false, kind: "basic_deal", promo: null });
  const [tab, setTab] = useState("list");

  const load = () => http.get(`/extranet/properties/${property.id}/promotions`).then((r) => setList(r.data));
  useEffect(() => {
    load();
    http.get(`/extranet/properties/${property.id}/rooms`).then((r) => setRooms(r.data));
    http.get(`/extranet/properties/${property.id}/rate-plans`).then((r) => setPlans(r.data));
  }, [property.id]); // eslint-disable-line

  const toggle = async (p) => {
    try { await http.put(`/extranet/promotions/${p.id}`, { ...p, active: !p.active }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Hapus promosi ${p.name}?`)) return;
    await http.delete(`/extranet/promotions/${p.id}`); toast.success("Promosi dihapus"); load();
  };
  const add = (kind) => setDlg({ open: true, kind, promo: null });

  return (
    <div className="fade-up flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{t("Promosi")}</h1>
          <p className="text-sm text-slate-500">{t("Diskon yang bisa digabung dihitung bertumpuk: 100 → Basic deal 10% = 90 → Mobile 10% = 81.")}</p>
        </div>
        <div className="flex rounded-md border border-slate-300 bg-white p-1">
          <button data-testid="promo-tab-list" onClick={() => setTab("list")} className={`rounded px-3 py-1.5 text-sm font-semibold ${tab === "list" ? "bg-[#006CE4] text-white" : "text-slate-600"}`}>{t("Promosi saya")} ({list.length})</button>
          <button data-testid="promo-tab-new" onClick={() => setTab("new")} className={`flex items-center gap-1 rounded px-3 py-1.5 text-sm font-semibold ${tab === "new" ? "bg-[#006CE4] text-white" : "text-slate-600"}`}><Plus className="h-4 w-4" /> {t("Buat promosi")}</button>
        </div>
      </div>

      {tab === "new" && PROMO_GROUPS.map((g) => (
        <Panel key={g.title} testid={`promo-group-${g.kinds[0]}`}>
          <div className="border-b border-slate-100 px-5 py-4"><h2 className="text-lg font-extrabold">{g.title}</h2><p className="text-sm text-slate-500">{g.sub}</p></div>
          {g.kinds.map((k) => <PromoTypeRow key={k} kind={k} onAdd={add} />)}
        </Panel>
      ))}

      {tab === "list" && (
        <Panel testid="promotions-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-left text-xs uppercase text-slate-500">
                <tr><th className="px-5 py-2.5">Nama</th><th>Tipe</th><th>Diskon</th><th>Syarat</th><th>Tanggal pesan</th><th>Tanggal inap</th><th>Aktif</th><th className="pr-5" /></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-400">Belum ada promosi. Klik "Buat promosi".</td></tr>}
                {list.map((p) => (
                  <tr key={p.id} data-testid={`promo-row-${p.id}`}>
                    <td className="px-5 py-3 font-semibold">{p.name}</td>
                    <td><span className="rounded bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-800">{PROMO_TYPES[p.kind]?.title}</span></td>
                    <td className="font-bold text-[#DC2626]">-{p.discount_percent}%</td>
                    <td className="font-mono-x text-xs">{condition(p)}</td>
                    <td className="text-xs">{range(p.booking_start, p.booking_end)}</td>
                    <td className="text-xs">{range(p.stay_start, p.stay_end)}</td>
                    <td><Switch data-testid={`promo-toggle-${p.id}`} checked={p.active} onCheckedChange={() => toggle(p)} /></td>
                    <td className="whitespace-nowrap pr-5 text-right">
                      <button data-testid={`promo-edit-${p.id}`} onClick={() => setDlg({ open: true, kind: p.kind, promo: p })} className="rounded p-2 text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`promo-delete-${p.id}`} onClick={() => del(p)} className="rounded p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
      <PromotionDialog open={dlg.open} onOpenChange={(o) => { setDlg({ ...dlg, open: o }); if (!o) setTab("list"); }} kind={dlg.kind} promo={dlg.promo}
        rooms={rooms} plans={plans} propertyId={property.id} onSaved={load} />
    </div>
  );
}
